import { Suspense, lazy, type ComponentType } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { AuthAppLayout } from '../AuthAppLayout'
import { AuthLayout } from '../AuthLayout'
import { LoginLayout } from '../LoginLayout'
import { ErrorBoundary } from '../components/ErrorBoundary/ErrorBoundary'
import { FullPageSpinner } from '../components/FullPageSpinner'
import { useRedirectUrl } from '../hooks'
import { isChunkLoadError, recoverFromStaleBundle } from './core/services/stale-bundle'
import { CalendarPage } from './calendar/pages/CalendarPage'
import { HomePage } from './creative/pages/HomePage'
import { ComposerPage } from './creative/pages/ComposerPage'
import { PostArrivalPage } from './creative/pages/PostArrivalPage'
import { PostsPage } from './creative/pages/PostsPage'
import { BrandKitPage } from './creative/pages/BrandKitPage'
import { ApiKeysPage } from './api-keys/ApiKeysPage'
import { CollaboratorsPage } from './creative/pages/CollaboratorsPage'
import { BillingPage } from './creative/pages/BillingPage'
import { AcceptInvitePage } from './authentication/pages/AcceptInvitePage'
import { ProfilePage } from './authentication/pages/ProfilePage'
import { ReferralPage } from './referrals/ReferralPage'
import { FacebookPagePickerPage } from './integrations/pages/FacebookPagePickerPage'
import { GatewayConnectCompletePage } from './integrations/pages/GatewayConnectCompletePage'
import { InstagramAccountConfirmPage } from './integrations/pages/InstagramAccountConfirmPage'
import { IntegrationsPage } from './integrations/pages/IntegrationsPage'
import { LinkedInTargetPickerPage } from './integrations/pages/LinkedInTargetPickerPage'
import { CliSignupSuccess } from './onboarding/components/CliSignupSuccess'

// When a client deploy rolls forward, tabs that loaded the previous bundle
// still reference now-deleted chunk hashes. A dynamic `import()` then 404s
// and the Suspense fallback paints silently — the user sees a blank screen
// (error tracking: 8+ "Failed to fetch dynamically imported module" groups going
// back to 2026-03-03). The reload policy lives in stale-bundle.ts (shared with
// the window-level `vite:preloadError` guard); this wrapper only keeps
// Suspense pending while the reload lands so the oops dialog never flashes.
function lazyWithChunkRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy<T>(() =>
    factory().catch(err => {
      if (!isChunkLoadError(err)) throw err
      if (recoverFromStaleBundle(err, { source: 'lazy-route' }) !== 'reloading') throw err
      // Keep Suspense pending while the reload fires — never resolve.
      return new Promise<{ default: T }>(() => {})
    })
  )
}

const OnboardingPage = lazyWithChunkRetry(() =>
  import('./onboarding').then(m => ({ default: m.OnboardingPage }))
)
const OnboardingLoginRedirect = lazyWithChunkRetry(() =>
  import('./onboarding/components').then(m => ({ default: m.OnboardingLoginRedirect }))
)
// Add-another-business takeover. Lives outside AuthAppLayout so it gets a
// fullscreen shell (no sidebar) while the user reruns the scan/study/plan
// flow against the new draft business.
const NewBusinessSetupPage = lazyWithChunkRetry(() =>
  import('./onboarding/dark/NewBusinessSetupPage').then(m => ({ default: m.NewBusinessSetupPage }))
)

// Design system preview surface. Lazy + DEV-only so the bundle stays clean
// in prod. Spec → playbook/design-system/.
const PreviewPage = lazyWithChunkRetry(() =>
  import('./preview').then(m => ({ default: m.PreviewPage }))
)

// Comment automation wizard — gated behind `engagement_section`, lazy so the
// module stays out of the main bundle while the rollout is staged.
const EngagementPage = lazyWithChunkRetry(() =>
  import('./engagement/pages/EngagementPage').then(m => ({ default: m.EngagementPage }))
)

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  // ErrorBoundary catches render-time crashes inside the lazy route (silent
  // before this; the audit on 2026-05-31 found no boundary anywhere on
  // /businesses/new). showOopsDialog gives the user a reload affordance
  // instead of an unrecoverable blank screen, and pipes the error to error tracking +
  // analytics (Errors.ERROR_OCCURRED).
  return (
    <ErrorBoundary showOopsDialog>
      <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

// Legacy `/project/:id` and `/post/:id` URLs land here from cached deep links;
// the composer is now the studio of a Post and owns those IDs directly.
function RedirectToCompose() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/compose/${id}`} replace />
}

// Logged-out catch-all. A plain `Navigate to="/"` here destroyed deep links
// (e.g. an emailed /calendar link) before the existing redirect plumbing could
// see them — the user logged in and landed on Home. Carry the intended path as
// `redirectUrl`, which LoginLayout/LoginForm already honor post-login (with
// LoginForm's open-redirect guard validating it stays on our origin).
function RedirectToLoginPreservingPath() {
  const { redirectUrl } = useRedirectUrl()
  return <Navigate to={`/login?redirectUrl=${redirectUrl}`} replace />
}

const DevOnlyRoutes = import.meta.env.DEV
  ? [
      {
        path: '/_preview',
        element: (
          <SuspenseWrapper>
            <PreviewPage />
          </SuspenseWrapper>
        ),
      },
    ]
  : []

// With Outlet for authenticated and unauthenticated users
const SharedRoutes = [{ path: '/login', element: <LoginLayout /> }, ...DevOnlyRoutes]

const get = (isLoggedIn: boolean) => {
  if (isLoggedIn) {
    return [
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/onboarding',
            element: (
              <SuspenseWrapper>
                <OnboardingPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/businesses/new',
            element: (
              <SuspenseWrapper>
                <NewBusinessSetupPage />
              </SuspenseWrapper>
            ),
          },
          // Full-screen invite landing (outside AuthAppLayout so it has no
          // sidebar); logged-in visitors can Accept or create their own business.
          // Token form = from an emailed link; token-less = a freshly-signed-in
          // invitee who came through normal signup (pulls their pending invite).
          { path: '/accept-invite/:token', element: <AcceptInvitePage /> },
          { path: '/accept-invite', element: <AcceptInvitePage /> },
          { path: '/cli-signup-success', element: <CliSignupSuccess /> },
          ...SharedRoutes,
        ],
      },
      {
        // Main app — AuthAppLayout renders AppLayout with Outlet for child routes
        element: <AuthAppLayout />,
        children: [
          { path: '/', element: <HomePage /> },
          { path: '/calendar', element: <CalendarPage /> },
          {
            path: '/engagement',
            element: (
              <SuspenseWrapper>
                <EngagementPage />
              </SuspenseWrapper>
            ),
          },
          { path: '/posts', element: <PostsPage /> },
          { path: '/creatives', element: <Navigate to="/posts" replace /> },
          { path: '/autopilot', element: <Navigate to="/calendar" replace /> },
          // Legacy redirect: the wizard / plan sheet have been replaced by a
          // small Defaults popover anchored on the cockpit. `?plan=open` is
          // the cockpit's signal to pop it open on mount. Kept for cached
          // deep links.
          { path: '/autopilot/setup', element: <Navigate to="/?plan=open" replace /> },
          { path: '/compose/:id/:platform?', element: <ComposerPage /> },
          { path: '/project/:id', element: <RedirectToCompose /> },
          // Arrival view: where a creation intent lands (and any post deep
          // link). The old redirect-to-compose behavior is superseded — the
          // arrival view shows the post with Edit-in-composer one tap away.
          { path: '/post/:id', element: <PostArrivalPage /> },
          { path: '/brand', element: <BrandKitPage /> },
          { path: '/integrations', element: <IntegrationsPage /> },
          { path: '/integrations/linkedin/select', element: <LinkedInTargetPickerPage /> },
          { path: '/integrations/facebook/select', element: <FacebookPagePickerPage /> },
          { path: '/integrations/instagram/confirm', element: <InstagramAccountConfirmPage /> },
          { path: '/integrations/gateway/complete', element: <GatewayConnectCompletePage /> },
          { path: '/collaborators', element: <CollaboratorsPage /> },
          { path: '/billing', element: <BillingPage /> },
          { path: '/api-keys', element: <ApiKeysPage /> },
          { path: '/profile', element: <ProfilePage /> },
          { path: '/referrals', element: <ReferralPage /> },
        ],
      },
      // Catch-all: redirect any old routes to root
      { path: '*', element: <Navigate to="/" replace /> },
    ]
  }

  return [
    { path: '/', element: <LoginLayout /> },
    // Onboarding route for unauthenticated users - captures URL state and redirects to login
    {
      path: '/onboarding',
      element: (
        <SuspenseWrapper>
          <OnboardingLoginRedirect />
        </SuspenseWrapper>
      ),
    },
    { path: '/cli-signup-success', element: <CliSignupSuccess /> },
    // Public invite landing: a logged-out invitee previews the invite and is
    // sent to /login with a redirect back here. Explicit path beats the '*'
    // catch-all below.
    { path: '/accept-invite/:token', element: <AcceptInvitePage /> },
    ...SharedRoutes,

    // Unknown or deep-linked path while logged out: preserve it through login.
    { path: '*', element: <RedirectToLoginPreservingPath /> },
  ]
}

export { get }
